// Navbar.js
import React, { useState, useEffect, useRef } from 'react';
import { Navbar, Nav, Container } from 'react-bootstrap';
import { FaLock } from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './Navbar.css';
import logo from '../assets/bientech-logo.png';

const NavBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const navbarToggleRef = useRef(null);

  // Efeito para gerenciar a classe menu-open no body
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setScrolled(offset > 50);

      // Aplicar scroll spy apenas na home
      if (location.pathname === '/') {
        const sections = ['home', 'solutions', 'team', 'contact'];
        let currentSection = '';
        sections.forEach(sectionId => {
          const element = document.getElementById(sectionId);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 100 && rect.bottom >= 0) {
              currentSection = sectionId;
            }
          }
        });
        setActiveLink(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Executa uma vez para definir o estado inicial de scrolled
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  // Atualiza o activeLink quando mudar de rota
  useEffect(() => {
    if (location.pathname === '/produtos') {
      setActiveLink('produtos');
    } else if (location.pathname === '/downloads') {
      setActiveLink('downloads');
    } else if (location.pathname === '/educacional') {
      setActiveLink('educacional');
    } else if (location.pathname === '/planos') {
      setActiveLink('planos');
    } else if (location.pathname === '/area-cliente') {
      setActiveLink('area-cliente');
    } else if (location.pathname === '/') {
      // Se voltar para a home, não define nenhum activeLink
      setActiveLink('');
    }
  }, [location.pathname]);

  // Efeito para rolar para o topo quando mudar de página
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const closeMenu = () => {
    if (window.innerWidth < 992) { // 992px é o breakpoint lg do Bootstrap
      navbarToggleRef.current.click();
      setIsMenuOpen(false);
    }
  };

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, 0);
    }
  };

  const scrollToSection = (sectionId) => {
    setActiveLink(sectionId);
    closeMenu();
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <Navbar 
      expand="lg" 
      fixed="top" 
      className={`custom-navbar ${scrolled ? 'scrolled' : ''}`}
    >
      <Container>
        <Navbar.Brand onClick={handleLogoClick} style={{ cursor: 'pointer' }}>
          <img src={logo} alt="Bientech Logo" className="navbar-logo" />
        </Navbar.Brand>
        
        <Navbar.Toggle 
          ref={navbarToggleRef} 
          aria-controls="basic-navbar-nav"
          onClick={handleMenuToggle}
        >
          <span className="navbar-toggler-icon"></span>
        </Navbar.Toggle>
        
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto">
            <Nav.Link 
              onClick={() => scrollToSection('home')} 
              className={`nav-link ${activeLink === 'home' ? 'active' : ''}`}
            >
              Home
            </Nav.Link>
            <Nav.Link 
              onClick={() => scrollToSection('solutions')} 
              className={`nav-link ${activeLink === 'solutions' ? 'active' : ''}`}
            >
              Soluções
            </Nav.Link>
            <Nav.Link 
              onClick={() => scrollToSection('team')} 
              className={`nav-link ${activeLink === 'team' ? 'active' : ''}`}
            >
              Equipe
            </Nav.Link>
            <Nav.Link 
              onClick={() => scrollToSection('contact')} 
              className={`nav-link ${activeLink === 'contact' ? 'active' : ''}`}
            >
              Contato
            </Nav.Link>
            <Nav.Link 
              as={Link} 
              to="/produtos" 
              className={`nav-link ${activeLink === 'produtos' ? 'active' : ''}`}
              onClick={() => {
                setActiveLink('produtos');
                closeMenu();
                window.scrollTo(0, 0);
              }}
            >
              Produtos
            </Nav.Link>
            <Nav.Link 
              as={Link} 
              to="/educacional" 
              className={`nav-link ${activeLink === 'educacional' ? 'active' : ''}`}
              onClick={() => {
                setActiveLink('educacional');
                closeMenu();
                window.scrollTo(0, 0);
              }}
            >
              Educacional
            </Nav.Link>
            <Nav.Link 
              as={Link} 
              to="/downloads" 
              className={`nav-link ${activeLink === 'downloads' ? 'active' : ''}`}
              onClick={() => {
                setActiveLink('downloads');
                closeMenu();
                window.scrollTo(0, 0);
              }}
            >
              Downloads
            </Nav.Link>
            <Nav.Link 
              as={Link} 
              to="/planos" 
              className={`nav-link ${activeLink === 'planos' ? 'active' : ''}`}
              onClick={() => {
                setActiveLink('planos');
                closeMenu();
                window.scrollTo(0, 0);
              }}
            >
              Planos
            </Nav.Link>
            <Nav.Link 
              as={Link} 
              to="/area-cliente" 
              className={`nav-link nav-cta ${activeLink === 'area-cliente' ? 'active' : ''}`}
              onClick={() => {
                setActiveLink('area-cliente');
                closeMenu();
                window.scrollTo(0, 0);
              }}
            >
              <FaLock className="nav-link-icon" />
              Área do Cliente
            </Nav.Link>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
}

export default NavBar;
